import { normalizeRational, type Equation } from './algebra.ts';

export interface ParsedEquationDraft {
  draftEquation: string;
  equation: Equation;
}

export type SpokenMathDetection =
  | ({ kind: 'equation' } & ParsedEquationDraft)
  | { kind: 'value'; value: string; unit?: string }
  | { kind: 'explanation'; text: string }
  | { kind: 'none' };

const MAX_INPUT_LENGTH = 500;
const MAX_TERMS_PER_SIDE = 8;
const smallNumbers: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};
const tens: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};
const numberPhrase = new RegExp(
  `\\b(?:negative\\s+)?(?:(?:${Object.keys(tens).join('|')})(?:[ -](?:${Object.keys(smallNumbers).slice(1, 10).join('|')}))?|(?:${Object.keys(smallNumbers).join('|')}))\\b`,
  'gi',
);

const parseNumberWords = (phrase: string): string => {
  const words = phrase.toLowerCase().replace(/-/g, ' ').split(/\s+/);
  const negative = words[0] === 'negative';
  const values = negative ? words.slice(1) : words;
  let value = 0;
  for (const word of values) value += smallNumbers[word] ?? tens[word] ?? 0;
  return `${negative ? '-' : ''}${value}`;
};

const stripProposalPrefix = (input: string): string => {
  let value = input.trim();
  const prefix =
    /^(?:(?:well|okay|so)\s+)?(?:i\s+(?:think|believe|guess)(?:\s+that)?(?:\s+the\s+equation\s+(?:should\s+be|is))?|the\s+equation\s+(?:should\s+be|is)|my\s+(?:equation|model)\s+is|i\s+(?:wrote|have|got))\s*[:,;-]?\s*/i;
  while (prefix.test(value)) value = value.replace(prefix, '');
  return value;
};

const normalizeSpokenSource = (input: string): string => {
  if (typeof input !== 'string' || input.trim().length === 0)
    throw new Error('Enter an equation.');
  if (input.length > MAX_INPUT_LENGTH)
    throw new Error('The equation draft is too long.');
  return stripProposalPrefix(input)
    .toLowerCase()
    .replace(/[–—−]/g, '-')
    .replace(/[×·]/g, '*')
    .replace(/\bis\s+equal\s+to\b/g, '=')
    .replace(/\bequals?\b/g, '=')
    .replace(/\bplus\b/g, '+')
    .replace(/\bminus\b/g, '-')
    .replace(/\bmultiplied\s+by\b|\btimes\b/g, '*')
    .replace(/\bover\b/g, '/')
    .replace(numberPhrase, parseNumberWords)
    .replace(/\$|\bdollars?\b/g, '')
    .replace(/[?.!,;:]\s*$/g, '')
    .trim();
};

interface RationalParts {
  numerator: bigint;
  denominator: bigint;
}

const rationalParts = (value: string): RationalParts => {
  const normalized = normalizeRational(value);
  const [numerator, denominator = '1'] = normalized.split('/');
  return { numerator: BigInt(numerator), denominator: BigInt(denominator) };
};

const addRational = (left: string, right: string): string => {
  const a = rationalParts(left);
  const b = rationalParts(right);
  return normalizeRational(
    `${a.numerator * b.denominator + b.numerator * a.denominator}/${a.denominator * b.denominator}`,
  );
};

type DraftTerm =
  { kind: 'x'; value: string } | { kind: 'constant'; value: string };

const isNegative = (value: string): boolean => value.startsWith('-');
const magnitude = (value: string): string =>
  isNegative(value) ? value.slice(1) : value;

const termText = (term: DraftTerm): string => {
  if (term.kind === 'constant') return magnitude(term.value);
  const coefficient = magnitude(term.value);
  return coefficient === '1' ? 'x' : `${coefficient}x`;
};

const displayTerms = (terms: DraftTerm[]): string =>
  terms
    .map((term, index) => {
      const body = termText(term);
      if (index === 0) return isNegative(term.value) ? `-${body}` : body;
      return `${isNegative(term.value) ? '-' : '+'} ${body}`;
    })
    .join(' ');

const parseSide = (
  input: string,
): { x: string; constant: string; display: string } => {
  const compact = input.replace(/\s+/g, '');
  if (!compact)
    throw new Error('Both sides of the equation need an expression.');
  if (/[^0-9x+\-*/.]/.test(compact))
    throw new Error('Use only numbers, x, and arithmetic symbols.');
  const sources = compact.match(/[+-]?[^+-]+/g);
  if (!sources || sources.join('') !== compact)
    throw new Error('The equation contains an incomplete term.');
  if (sources.length > MAX_TERMS_PER_SIDE)
    throw new Error('Use at most eight terms on each side.');

  let x = '0';
  let constant = '0';
  const terms: DraftTerm[] = [];
  for (const source of sources) {
    const sign = source.startsWith('-') ? '-' : '';
    const unsigned = source.replace(/^[+-]/, '');
    const variable = unsigned.match(/^((?:\d+(?:\.\d+)?|\d+\/\d+)\*?)?x$/);
    if (variable) {
      const coefficientSource = (variable[1] ?? '1').replace(/\*$/, '');
      const value = normalizeRational(`${sign}${coefficientSource}`);
      x = addRational(x, value);
      terms.push({ kind: 'x', value });
      continue;
    }
    if (!/^(?:\d+(?:\.\d+)?|\d+\/\d+)$/.test(unsigned))
      throw new Error('Each term must be a number or a multiple of x.');
    const value = normalizeRational(`${sign}${unsigned}`);
    constant = addRational(constant, value);
    terms.push({ kind: 'constant', value });
  }
  return { x, constant, display: displayTerms(terms) };
};

export const parseLinearEquationDraft = (
  input: string,
): ParsedEquationDraft => {
  const normalized = normalizeSpokenSource(input);
  const equalsCount = [...normalized].filter(
    (character) => character === '=',
  ).length;
  if (equalsCount !== 1)
    throw new Error('Write one equals sign with an expression on each side.');
  const [leftSource, rightSource] = normalized.split('=');
  const left = parseSide(leftSource);
  const right = parseSide(rightSource);
  return {
    draftEquation: `${left.display} = ${right.display}`,
    equation: {
      left: { x: left.x, constant: left.constant },
      right: { x: right.x, constant: right.constant },
    },
  };
};

export const detectSpokenMath = (
  input: string,
  units: readonly string[] = ['hours', 'dollars', 'bikes'],
): SpokenMathDetection => {
  const text = typeof input === 'string' ? input.trim() : '';
  if (!text) return { kind: 'none' };
  if (text.length > MAX_INPUT_LENGTH)
    return { kind: 'explanation', text: text.slice(0, MAX_INPUT_LENGTH) };

  if (/=|\bequals?\b|\bis\s+equal\s+to\b/i.test(text)) {
    try {
      return { kind: 'equation', ...parseLinearEquationDraft(text) };
    } catch {
      // A question containing equality language remains learner text.
    }
  }

  const valueMatch = stripProposalPrefix(text)
    .toLowerCase()
    .replace(numberPhrase, parseNumberWords)
    .match(
      /^(?:my\s+answer\s+is|the\s+answer\s+is|x\s+is|answer)\s+(-?\d+(?:\.\d+)?|-?\d+\/\d+)(?:\s+([a-z][a-z -]{0,40}))?[.!?]?$/,
    );
  if (valueMatch) {
    const canonicalUnit = (value: string) =>
      value.toLowerCase().trim().replace(/s$/, '');
    if (
      valueMatch[2] &&
      !units.some(
        (unit) => canonicalUnit(unit) === canonicalUnit(valueMatch[2]),
      )
    )
      return { kind: 'explanation', text: text.slice(0, MAX_INPUT_LENGTH) };
    try {
      return {
        kind: 'value',
        value: normalizeRational(valueMatch[1]),
        ...(valueMatch[2] ? { unit: valueMatch[2] } : {}),
      };
    } catch {
      // Keep malformed values as ordinary learner text.
    }
  }
  return { kind: 'explanation', text: text.slice(0, MAX_INPUT_LENGTH) };
};
